import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/auth";

import { getFirebaseBackend } from "../../authUtils";
import { normalizarUsuario } from "@/services/usuarios";
import {
  consultarSuscripcionPuntual,
  resolverEstadoMembresia,
  parseToSeconds,
} from "@/services/suscripciones";

/**
 * Sesión del cliente en el portal web.
 *
 * Es el espejo del módulo `empleado`, pero contra `app_usuarios`: los clientes
 * entran con la misma cuenta que usan en la app móvil, no con una cuenta nueva.
 *
 * La membresía se consulta **por usuario** (`/customer/{email}` →
 * `/subscription/{id}`), igual que hace la app. Nunca con el índice masivo
 * `/users/subscriptions`: ese endpoint devuelve el padrón completo con todos
 * los correos, y en una página de cliente quedaría a la vista en el navegador.
 */

export const state = {
  perfil: null,
  membresia: null,
  correoCargado: null,
  cargando: false,
};

export const mutations = {
  SET_CLIENTE(state, { perfil, correo }) {
    state.perfil = perfil;
    state.correoCargado = correo;
  },
  SET_MEMBRESIA(state, membresia) {
    state.membresia = membresia;
  },
  SET_CARGANDO(state, valor) {
    state.cargando = valor;
  },
  LIMPIAR(state) {
    state.perfil = null;
    state.membresia = null;
    state.correoCargado = null;
  },
};

export const getters = {
  autorizado: (state) => !!state.perfil,
  nombre: (state) => (state.perfil ? state.perfil.name || state.perfil.email : ""),
  tieneMembresia: (state) => !!(state.membresia && state.membresia.activo),
  estadoMembresia: (state) => state.membresia,
  /** Los comercios ven el área "Mi empresa"; los usuarios normales no. */
  esComercio: (state) => !!state.perfil && state.perfil.tipo === "comercio",
  /** El perfil queda incompleto si el usuario nunca terminó el alta en la app. */
  perfilIncompleto: (state) => !!state.perfil && state.perfil.estadoRegistro !== "completado",
};

export const actions = {
  /**
   * Carga el perfil del cliente autenticado y su estado de membresía.
   * Reutiliza la caché mientras se trate del mismo correo.
   */
  async cargar({ commit, state, dispatch }, { force = false } = {}) {
    // Espera a que Firebase restaure la sesión (en un recargado, el guard corre
    // antes de que `currentUser` esté listo).
    await getFirebaseBackend().esperarAuthListo();
    const usuario = firebase.auth().currentUser;
    const correo = usuario && usuario.email ? usuario.email.toLowerCase() : null;

    if (!correo) {
      commit("LIMPIAR");
      return null;
    }
    if (!force && state.correoCargado === correo) return state.perfil;

    commit("SET_CARGANDO", true);
    try {
      const snap = await firebase
        .firestore()
        .collection("app_usuarios")
        .where("email", "==", correo)
        .limit(1)
        .get();

      const perfil = snap.empty
        ? null
        : normalizarUsuario({ id: snap.docs[0].id, ...snap.docs[0].data() });

      commit("SET_CLIENTE", { perfil, correo });
      if (perfil) await dispatch("cargarMembresia");
      return perfil;
    } catch (err) {
      // Un fallo de red no debe sacar del portal a quien ya estaba dentro.
      console.warn("[cliente] no se pudo cargar el perfil:", err);
      return state.perfil;
    } finally {
      commit("SET_CARGANDO", false);
    }
  },

  async cargarMembresia({ commit, state }) {
    if (!state.perfil) return null;
    try {
      const sub = await consultarSuscripcionPuntual({
        email: state.perfil.email,
        stripeId: state.perfil.stripe_id || null,
      });
      const membresia = resolverEstadoMembresia(
        sub
          ? {
              active: String(sub.status || "").toLowerCase() === "active",
              status: String(sub.status || "").toLowerCase(),
              current_period_end: parseToSeconds(sub.current_period_end),
              cancel_at_period_end: !!sub.cancel_at_period_end,
            }
          : null
      );
      commit("SET_MEMBRESIA", membresia);
      return membresia;
    } catch (err) {
      console.warn("[cliente] no se pudo consultar la membresía:", err);
      // Sin respuesta de Stripe se asume sin membresía: es preferible mostrar la
      // invitación a Plus que regalar el contenido de paga por un error de red.
      commit("SET_MEMBRESIA", resolverEstadoMembresia(null));
      return null;
    }
  },

  limpiar({ commit }) {
    commit("LIMPIAR");
  },
};
